import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowRight, Lock, Play, Search, Radio, Bookmark, BookmarkCheck, Crown, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import api from '@/lib/api';
import { getToken } from '@/lib/authToken';
import FwmShell from '@/components/fwm/FwmShell';
import { FwmHero } from '@/components/fwm/FwmHero';
import { useHero } from '@/components/fwm/useHero';
import { FWM_IMAGERY } from '@/components/fwm/imagery';
import { LiveStream } from '@/components/live/LiveStream';

const youTubeId = (url = '') => {
  if (url.includes('youtu.be/')) return url.split('youtu.be/')[1].split(/[?/]/)[0];
  if (url.includes('v=')) return url.split('v=')[1].split('&')[0];
  for (const m of ['/live/', '/embed/', '/shorts/']) {
    if (url.includes(m)) return url.split(m)[1].split('?')[0];
  }
  return null;
};

const Card = ({ item, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(item)}
    data-testid={`fwtv-card-${item.id}`}
    className="group text-left rounded-xl overflow-hidden border border-white/10 bg-white/[0.03] hover:border-[#C9992B]/50 transition-transform duration-200 hover:-translate-y-1"
  >
    <div
      className="relative aspect-video bg-[#1C1A16] bg-cover bg-center"
      style={item.thumbnail_url ? { backgroundImage: `url(${item.thumbnail_url})` } : undefined}
    >
      {item.locked ? (
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded bg-[#C9992B] px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-[#141311]">
          <Lock className="h-3 w-3" /> Premium
        </span>
      ) : item.state === 'live' || item.category === 'live' ? (
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded bg-rose-600 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
          <Radio className="h-3 w-3" /> Live
        </span>
      ) : null}
      <span className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
        <Play className="h-10 w-10 text-white drop-shadow" />
      </span>
      {item.resume_position > 0 && item.duration_seconds ? (
        <span className="absolute bottom-0 left-0 h-1 bg-[#C9992B]"
              style={{ width: `${Math.min(100, (item.resume_position / item.duration_seconds) * 100)}%` }} />
      ) : null}
    </div>
    <div className="p-3">
      <p className="text-sm font-semibold text-slate-100 leading-snug line-clamp-2">{item.title}</p>
      <p className="text-xs text-slate-400 mt-1 capitalize">
        {item.locked ? 'Subscribers only' : (item.category || 'sermon')}
      </p>
    </div>
  </button>
);

const Player = ({ item, onClose, signedIn, onSubscribe }) => {
  const videoRef = useRef(null);
  const qc = useQueryClient();

  const { data: full } = useQuery({
    queryKey: ['fwtv-item', item?.id, signedIn],
    queryFn: () => api
      .get(signedIn ? `/fwtv/tv/item/${item.id}` : `/fwtv/public/tv/item/${item.id}`)
      .then((r) => r.data),
    enabled: !!item?.id && item.category !== 'live',
  });

  const playable = item.category === 'live' ? item : (full || item);
  const yt = youTubeId(playable.video_url || '');

  const saveToggle = useMutation({
    mutationFn: () => api.post(`/fwtv/premium/save/${item.id}`).then((r) => r.data),
    onSuccess: (d) => {
      toast.success(d.saved ? 'Saved to My Library' : 'Removed from My Library');
      qc.invalidateQueries({ queryKey: ['fwtv-item', item.id] });
      qc.invalidateQueries({ queryKey: ['fwtv-library'] });
    },
    onError: () => toast.error('Please sign in to save videos'),
  });

  // resume + progress tracking for file-based playback
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !signedIn) return undefined;
    if (playable.resume_position) el.currentTime = playable.resume_position;
    const timer = setInterval(() => {
      if (!el.paused && el.currentTime > 1) {
        api.post('/fwtv/premium/progress', {
          item_id: item.id,
          position_seconds: Math.floor(el.currentTime),
          duration_seconds: Math.floor(el.duration || 0) || null,
        }).catch(() => {});
      }
    }, 15000);
    return () => clearInterval(timer);
  }, [playable.resume_position, playable.video_url, signedIn, item.id]);

  const locked = playable.locked;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-[#141311] border-white/10 text-slate-100 p-0 overflow-hidden"
                     data-testid="fwtv-player">
        <button onClick={onClose} data-testid="fwtv-player-close"
                className="absolute right-3 top-3 z-10 rounded-full bg-black/50 p-2 hover:bg-black/70">
          <X className="h-4 w-4" />
        </button>
        {locked ? (
          <div className="p-10 text-center space-y-4" data-testid="fwtv-paywall">
            <Crown className="h-10 w-10 mx-auto text-[#C9992B]" />
            <h3 className="text-xl font-semibold">{playable.title}</h3>
            <p className="text-sm text-slate-400 max-w-md mx-auto">
              This is part of Faith Works TV Premium. Subscribe to watch the full
              series, courses and the complete archive.
            </p>
            <Button onClick={onSubscribe} data-testid="fwtv-paywall-subscribe"
                    className="bg-[#C9992B] text-[#141311] hover:bg-[#A67816] rounded-full px-6">
              Subscribe to Premium
            </Button>
          </div>
        ) : (
          <>
            <div className="aspect-video bg-black">
              {playable.category === 'live' && playable.transport === 'livekit' ? (
                /* Broadcast through the platform's own streaming server — the
                   room, not a link, is the source. */
                <LiveStream scope="fwtv" ident={playable.programme_id || playable.id}
                            title={playable.title} testId="fwtv-livekit" />
              ) : yt ? (
                <iframe title={playable.title} className="w-full h-full" allowFullScreen
                        src={`https://www.youtube.com/embed/${yt}?autoplay=1`}
                        allow="autoplay; encrypted-media; picture-in-picture" />
              ) : playable.video_url ? (
                <video ref={videoRef} src={playable.video_url} controls autoPlay
                       className="w-full h-full" data-testid="fwtv-video-el" />
              ) : (
                <div className="grid h-full place-items-center text-slate-500 text-sm">
                  No playable source on this item yet.
                </div>
              )}
            </div>
            <div className="p-5 flex items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold">{playable.title}</h3>
                <p className="text-sm text-slate-400 mt-1 line-clamp-3">{playable.description}</p>
              </div>
              {signedIn && item.category !== 'live' && (
                <Button variant="outline" size="sm" onClick={() => saveToggle.mutate()}
                        data-testid="fwtv-save-toggle"
                        className="border-white/20 bg-transparent hover:bg-white/10 shrink-0">
                  {playable.saved ? <BookmarkCheck className="h-4 w-4 mr-1" /> : <Bookmark className="h-4 w-4 mr-1" />}
                  {playable.saved ? 'Saved' : 'Save'}
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const SubscribeDialog = ({ open, onClose, premium, status, signedIn }) => {
  const qc = useQueryClient();
  const [plan, setPlan] = useState('annual');
  const [method, setMethod] = useState('stripe');
  const [phone, setPhone] = useState('');
  const [pending, setPending] = useState(null);

  const start = useMutation({
    mutationFn: () => api.post('/fwtv/premium/checkout', {
      plan, method, phone, wallet: 'ecocash', origin_url: window.location.origin,
    }).then((r) => r.data),
    onSuccess: (d) => {
      if (d.kind === 'stripe' && d.url) {
        window.location.href = d.url;
        return;
      }
      setPending(d);
      toast.success(d.instructions || 'Approve the prompt on your phone');
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not start checkout'),
  });

  const trial = useMutation({
    mutationFn: () => api.post('/fwtv/premium/trial').then((r) => r.data),
    onSuccess: (d) => {
      toast.success(`Your ${d.trial_days}-day free trial is on — enjoy the full library`);
      qc.invalidateQueries({ queryKey: ['fwtv-premium-status'] });
      qc.invalidateQueries({ queryKey: ['fwtv-tv'] });
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not start your trial'),
  });

  if (!open) return null;
  const price = plan === 'annual' ? premium?.annual_price : premium?.monthly_price;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-[#141311] border-white/10 text-slate-100"
                     data-testid="fwtv-subscribe-dialog">
        <div className="space-y-5">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Crown className="h-5 w-5 text-[#C9992B]" /> Faith Works TV Premium
            </h3>
            <p className="text-sm text-slate-400 mt-1">{premium?.blurb}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {['monthly', 'annual'].map((key) => (
              <button key={key} onClick={() => setPlan(key)}
                      data-testid={`fwtv-plan-${key}`}
                      className={`rounded-xl border p-4 text-left transition-colors ${
                        plan === key ? 'border-[#C9992B] bg-[#C9992B]/10' : 'border-white/15 hover:border-white/30'}`}>
                <p className="text-xs uppercase tracking-wider text-slate-400 capitalize">{key}</p>
                <p className="text-xl font-semibold mt-1">
                  {premium?.currency} {key === 'annual' ? premium?.annual_price : premium?.monthly_price}
                </p>
              </button>
            ))}
          </div>
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wider text-slate-400">Pay with</p>
            <div className="flex gap-2">
              <Button variant={method === 'stripe' ? 'default' : 'outline'} size="sm"
                      onClick={() => setMethod('stripe')} data-testid="fwtv-method-stripe"
                      className={method === 'stripe' ? 'bg-[#C9992B] text-[#141311]' : 'border-white/20 bg-transparent'}>
                Card
              </Button>
              <Button variant={method === 'paynow' ? 'default' : 'outline'} size="sm"
                      onClick={() => setMethod('paynow')} data-testid="fwtv-method-paynow"
                      className={method === 'paynow' ? 'bg-[#C9992B] text-[#141311]' : 'border-white/20 bg-transparent'}>
                EcoCash / Mobile
              </Button>
            </div>
            {method === 'paynow' && (
              <Input value={phone} onChange={(e) => setPhone(e.target.value)}
                     placeholder="Wallet phone number" data-testid="fwtv-phone-input"
                     className="bg-white/5 border-white/15" />
            )}
          </div>
          {pending ? (
            <p className="text-sm text-amber-300" data-testid="fwtv-pending-note">{pending.instructions}</p>
          ) : null}
          {!signedIn ? (
            <Button asChild data-testid="fwtv-subscribe-signin"
                    className="w-full bg-[#C9992B] text-[#141311] hover:bg-[#A67816] rounded-full">
              <Link to={`/login?next=${encodeURIComponent('/tv?subscribe=1')}`}>
                Sign in to subscribe
              </Link>
            </Button>
          ) : (
            <Button onClick={() => start.mutate()} disabled={start.isPending}
                    data-testid="fwtv-subscribe-confirm"
                    className="w-full bg-[#C9992B] text-[#141311] hover:bg-[#A67816] rounded-full">
              {start.isPending ? 'Starting…' : `Subscribe — ${premium?.currency} ${price}`}
            </Button>
          )}
          {signedIn && status?.trial_available && (
            <Button variant="outline" onClick={() => trial.mutate()} disabled={trial.isPending}
                    data-testid="fwtv-start-trial"
                    className="w-full border-white/20 bg-transparent hover:bg-white/10 rounded-full">
              {trial.isPending ? 'Starting…' : `Start ${status.trial_days}-day free trial`}
            </Button>
          )}
          <p className="text-[11px] text-slate-500 text-center">
            Cancel any time. Your subscription supports the ministry and is recorded in the FWM ledger.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function FaithWorksTVPage() {
  const [params, setParams] = useSearchParams();
  const [category, setCategory] = useState('all');
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [playing, setPlaying] = useState(null);
  const [subscribing, setSubscribing] = useState(params.get('subscribe') === '1');
  const [tab, setTab] = useState('browse');
  const signedIn = !!getToken();

  useEffect(() => {
    const t = setTimeout(() => setQ(term), 350);
    return () => clearTimeout(t);
  }, [term]);

  const { data, isLoading } = useQuery({
    queryKey: ['fwtv-tv', category, q, signedIn],
    queryFn: () => api.get(signedIn ? '/fwtv/tv' : '/fwtv/public/tv', {
      params: { limit: 48, ...(category !== 'all' ? { category } : {}), ...(q ? { q } : {}) },
    }).then((r) => r.data),
  });

  const { data: status } = useQuery({
    queryKey: ['fwtv-premium-status'],
    queryFn: () => api.get('/fwtv/premium/status').then((r) => r.data),
    enabled: signedIn,
    retry: false,
  });

  const { data: library } = useQuery({
    queryKey: ['fwtv-library'],
    queryFn: () => api.get('/fwtv/premium/my-library').then((r) => r.data),
    enabled: signedIn && tab === 'library',
    retry: false,
  });

  // confirm a return trip from checkout
  useEffect(() => {
    const ref = params.get('ref');
    if (params.get('premium') !== 'success' || !ref) return;
    api.get(`/fwtv/premium/checkout/status/${ref}`)
      .then((r) => {
        if (r.data?.active) toast.success('Welcome to Premium — enjoy the full library');
        else toast.info('Payment is still processing — we will unlock it as soon as it clears');
      })
      .catch(() => {})
      .finally(() => { params.delete('premium'); params.delete('ref'); setParams(params, { replace: true }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ?watch=<id> — the embed on faithworldministries.com links straight in
  useEffect(() => {
    const watch = params.get('watch');
    if (!watch) return;
    api.get(signedIn ? `/fwtv/tv/item/${watch}` : `/fwtv/public/tv/item/${watch}`)
      .then((r) => setPlaying(r.data))
      .catch(() => toast.error('That video is no longer available'))
      .finally(() => { params.delete('watch'); setParams(params, { replace: true }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // nudge the YouTube pull — the backend rate-limits itself to once per 15 min
  useEffect(() => {
    api.post('/fwtv/public/tv/youtube/auto-sync').catch(() => {});
  }, []);

  const live = useMemo(() => (data?.live || []).filter((l) => l.state === 'live'), [data]);
  const upcoming = useMemo(() => (data?.live || []).filter((l) => l.state === 'upcoming'), [data]);
  const premium = data?.premium;
  const hero = useHero('tv', { image_url: FWM_IMAGERY.choir });

  return (
    <FwmShell ctaLabel="Start watching" ctaLink="/tv" testId="fwtv-page" overDark>
      <FwmHero
        testId="tv-hero"
        image={hero.image_url}
        fallbackImage={hero.fallback_image_url}
        video={hero.video_url}
        mediaPriority={hero.media_priority}
        eyebrow={hero.eyebrow}
        title={hero.title}
        accent={hero.accent}
        blurb={hero.blurb}
        actions={(
          <>
            <button className="fwm-btn fwm-btn-lg" data-testid="fwtv-browse-btn"
                    onClick={() => { setTab('browse');
                      document.getElementById('watch')?.scrollIntoView({ behavior: 'smooth' }); }}>
              Start watching <ArrowRight className="h-4 w-4" />
            </button>
            {status?.active ? (
              <span className="fwm-btn-ondark !cursor-default" data-testid="fwtv-premium-badge">
                <Crown className="h-4 w-4" /> Premium
              </span>
            ) : (
              <button className="fwm-btn-ondark" data-testid="fwtv-subscribe-btn"
                      onClick={() => setSubscribing(true)}>
                <Crown className="h-4 w-4" /> Subscribe to Premium
              </button>
            )}
            {signedIn && (
              <button className="fwm-btn-ondark" data-testid="fwtv-tab-library"
                      onClick={() => { setTab(tab === 'library' ? 'browse' : 'library');
                        document.getElementById('watch')?.scrollIntoView({ behavior: 'smooth' }); }}>
                {tab === 'library' ? 'Browse everything' : 'My Library'}
              </button>
            )}
          </>
        )}
      />

      <section id="watch" className="fwm-section" data-testid="fwtv-watch">
        <div className="fwm-section-head">
          <div>
            <p className="fwm-kicker">Faith Works TV</p>
            <h2 className="mt-3">{tab === 'library' ? 'Your library' : 'The full channel'}</h2>
          </div>
          <p className="text-sm text-[color:var(--fwm-muted)]">
            Live services, teaching series and the whole archive
          </p>
        </div>

        <div className="fwm-dark-panel p-6 sm:p-9 text-slate-100 space-y-10">
        {tab === 'library' ? (
          <section className="space-y-8" data-testid="fwtv-library">
            {!library?.premium_active && (
              <p className="text-sm text-[#E8C878]">
                Your Premium subscription is not active — saved public videos still play.
              </p>
            )}
            {[['Continue watching', library?.continue_watching],
              ['Saved', library?.saved],
              ['Recently watched', library?.recently_watched]].map(([label, rows]) => (
              <div key={label}>
                <h3 className="text-lg mb-4">{label}</h3>
                {rows?.length ? (
                  <div className="grid gap-5 grid-cols-[repeat(auto-fill,minmax(220px,1fr))]">
                    {rows.map((item) => <Card key={`${label}-${item.id}`} item={item} onOpen={setPlaying} />)}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Nothing here yet.</p>
                )}
              </div>
            ))}
          </section>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-0 sm:min-w-[240px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input value={term} onChange={(e) => setTerm(e.target.value)}
                       placeholder="Search sermons, worship, programmes…"
                       data-testid="fwtv-search"
                       className="fwm-field fwm-field-ondark !pl-9" />
              </div>
              {[{ key: 'all', label: 'All' }, ...(data?.categories || [])].map((c) => (
                <button key={c.key} onClick={() => setCategory(c.key)}
                        data-testid={`fwtv-chip-${c.key}`}
                        data-active={category === c.key}
                        className="fwm-chip fwm-chip-ondark">
                  {c.label}
                  {c.count ? <span className="fwm-chip-count">{c.count}</span> : null}
                </button>
              ))}
            </div>

            {live.length > 0 && (
              <section data-testid="fwtv-live-rail">
                <h3 className="text-lg mb-4 flex items-center gap-2">
                  <Radio className="h-4 w-4 text-rose-500" /> Live now
                </h3>
                <div className="grid gap-5 grid-cols-[repeat(auto-fill,minmax(260px,1fr))]">
                  {live.map((item) => <Card key={item.id} item={item} onOpen={setPlaying} />)}
                </div>
              </section>
            )}

            {upcoming.length > 0 && (
              <section data-testid="fwtv-upcoming">
                <h3 className="text-lg mb-4">Coming up</h3>
                <ul className="space-y-2 text-sm text-slate-300">
                  {upcoming.map((s) => (
                    <li key={s.id} className="flex gap-3">
                      <span className="text-[#C9992B]">{new Date(s.start_at).toLocaleString()}</span>
                      <span>{s.title}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section>
              <h3 className="text-lg mb-4">
                {q ? `Results for "${q}"` : 'Watch'}
                {data?.total ? <span className="text-slate-500 font-normal text-sm"> · {data.total}</span> : null}
              </h3>
              {isLoading ? (
                <div className="grid gap-5 grid-cols-[repeat(auto-fill,minmax(220px,1fr))]">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="aspect-video rounded-xl bg-white/5 animate-pulse" />
                  ))}
                </div>
              ) : data?.items?.length ? (
                <div className="grid gap-5 grid-cols-[repeat(auto-fill,minmax(220px,1fr))]"
                     data-testid="fwtv-grid">
                  {data.items.map((item) => <Card key={item.id} item={item} onOpen={setPlaying} />)}
                </div>
              ) : (
                <p className="text-sm text-slate-500" data-testid="fwtv-empty">
                  Nothing here yet — check back soon.
                </p>
              )}
            </section>
          </>
        )}
        </div>
      </section>

      {premium?.enabled && !status?.active && (
        <section className="border-t border-[color:var(--fwm-line)]" data-testid="fwtv-premium-band">
          <div className="fwm-section !py-20 flex flex-col lg:flex-row lg:items-center
                          justify-between gap-10">
            <div className="max-w-xl">
              <p className="fwm-kicker">Faith Works TV Premium</p>
              <h2 className="fwm-display mt-3 text-3xl sm:text-4xl tracking-tight">
                The whole archive, courses and series
              </h2>
              <p className="mt-4 text-[color:var(--fwm-body)] leading-relaxed">
                {premium.blurb} · {premium.currency} {premium.monthly_price}/month or
                {' '}{premium.currency} {premium.annual_price}/year
              </p>
            </div>
            <button onClick={() => setSubscribing(true)} data-testid="fwtv-band-subscribe"
                    className="fwm-btn fwm-btn-lg shrink-0">
              Subscribe <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </section>
      )}

      {playing && (
        <Player item={playing} signedIn={signedIn} onClose={() => setPlaying(null)}
                onSubscribe={() => { setPlaying(null); setSubscribing(true); }} />
      )}
      <SubscribeDialog open={subscribing} premium={premium} status={status} signedIn={signedIn}
                       onClose={() => setSubscribing(false)} />
    </FwmShell>
  );
}
